import { useCallback, useState } from "react";

import { useGetVersionQuery } from "fond/api";
import { inAddress, inParcel, inPole, inSpan, inStreet } from "fond/layers";
import { isAcceptableSize, MAX_PLANNER_EXTENDED_ADDRESS } from "fond/project/polygon";
import { downloadLayersWithinArea } from "fond/project/polygon/download";
import { beginPolygonSelectUpload } from "fond/project/redux";
import { LayerId, LayerIds } from "fond/types";
import { BaseDataLayerName } from "fond/types/layer";
import { useAppDispatch, useAppSelector } from "fond/utils/hooks";

import { BaseDataLayerKey } from "../layers";

/** FOND data downloaded for a base data layer, and the planner layer each is imported into. */
const BOUNDARY_IMPORTS: Partial<Record<BaseDataLayerKey, { download: BaseDataLayerName; layerId: LayerId }[]>> = {
  addresses: [{ download: inAddress, layerId: LayerIds.inAddress }],
  parcels: [{ download: inParcel, layerId: LayerIds.inParcel }],
  streets: [{ download: inStreet, layerId: LayerIds.inBaseDataStreet }],
  aerial: [
    { download: inSpan, layerId: LayerIds.inSpan },
    { download: inPole, layerId: LayerIds.inPole },
  ],
};

export type ImportWithinBoundaryState = { status: "idle" } | { status: "downloading" } | { status: "uploading" } | { status: "error"; error: string };

export type ImportWithinBoundary = ImportWithinBoundaryState & {
  importWithinBoundary(): Promise<void>;
  reset(): void;
};

/** Return an action that imports the FOND data of a base data layer from inside the boundary. */
export const useImportWithinBoundary = (layerKey: BaseDataLayerKey, onSuccess: () => void): ImportWithinBoundary => {
  const dispatch = useAppDispatch();
  const { projectId, versionId } = useAppSelector((state) => state.project);
  const { data: version } = useGetVersionQuery(versionId, { skip: !versionId });
  const [state, setState] = useState<ImportWithinBoundaryState>({ status: "idle" });
  const boundary = version?.UploadedArea;

  const importWithinBoundary = useCallback(async () => {
    const imports = BOUNDARY_IMPORTS[layerKey];
    if (!boundary || !imports) return;

    if (!isAcceptableSize(boundary)) {
      setState({ status: "error", error: "The boundary is too large to import FOND data from." });
      return;
    }

    setState({ status: "downloading" });
    let result;
    try {
      result = await downloadLayersWithinArea(
        imports.map(({ download }) => download),
        boundary
      );
    } catch {
      setState({ status: "error", error: "There was a problem downloading FOND data for this boundary. Please try again." });
      return;
    }

    if (result.status === "QueryTooLarge") {
      setState({ status: "error", error: "There are too many features inside the boundary to import." });
      return;
    }
    if (result.status === "UnsupportedArea") {
      setState({ status: "error", error: "FOND doesn't have data for this area yet." });
      return;
    }
    if (layerKey === "addresses" && result.data[inAddress].features.length > MAX_PLANNER_EXTENDED_ADDRESS) {
      setState({
        status: "error",
        error: `The boundary contains more than ${MAX_PLANNER_EXTENDED_ADDRESS.toLocaleString()} addresses. Draw a smaller area to import addresses.`,
      });
      return;
    }

    const { data } = result;
    const files = Object.fromEntries(
      imports.map(({ download, layerId }) => [layerId, [new File([JSON.stringify(data[download])], `${layerId}.geojson`)]])
    );

    setState({ status: "uploading" });
    try {
      await dispatch(beginPolygonSelectUpload(projectId, versionId, files, boundary));
    } catch {
      setState({ status: "error", error: "There was a problem saving your data. Please try again." });
      return;
    }
    setState({ status: "idle" });
    onSuccess();
  }, [boundary, dispatch, layerKey, onSuccess, projectId, versionId]);

  const reset = useCallback(() => setState({ status: "idle" }), []);

  return { ...state, importWithinBoundary, reset };
};
